import { Appointment } from "@elixir/types";
import { useEffect, useMemo, useState } from "react";
import { Popover } from "antd";
import { PopoverContainerUi } from "ui";
import { updateAppointment } from "../../../../utils/bridges/methods";
import Chip from "../../../../atoms/chip";
import PopoverHeader from "../../../../atoms/popover/header";
import { getPaymentStatusForAppointment } from "../../../../organisms/appointments-v2/get-payment-status-of-appointment";
import { FirebaseAppointment } from "../../../../firestore/types";

export enum APPOINTMENT_PAYMENT_STATUS_V1 {
  PAID = "PAID",
  UNPAID = "UNPAID",
  PARTIALLY_PAID = "PARTIALLY_PAID",
  REFUNDED = "REFUNDED",
  FAILED = "FAILED",
}

const PAYMENT_STATUS_PILL_MAP: Partial<
  Record<APPOINTMENT_PAYMENT_STATUS_V1, { label: string; className: string }>
> = {
  [APPOINTMENT_PAYMENT_STATUS_V1.PAID]: {
    label: "Paid",
    className:
      "dr-text-darwin-touch-green-dark dr-border-darwin-touch-green-primary ",
  },
  [APPOINTMENT_PAYMENT_STATUS_V1.UNPAID]: {
    label: "Unpaid",
    className:
      "dr-text-darwin-touch-red-primary dr-border-darwin-touch-red-primary ",
  },
  [APPOINTMENT_PAYMENT_STATUS_V1.PARTIALLY_PAID]: {
    label: "Partially Paid",
    className:
      "dr-text-darwin-touch-yellow-primary border-darwin-accent-bright-yellow-900 ",
  },
};

const UNSET_PILL_CONFIG = {
  label: "Pay Status",
  className:
    "dr-text-darwin-primary-primary dr-border-darwin-primary dr-border-darwin-neutral-200",
};

const WHITELISTED_PAYMENT_STATUSES = [
  { label: "Paid", value: APPOINTMENT_PAYMENT_STATUS_V1.PAID },
  { label: "Unpaid", value: APPOINTMENT_PAYMENT_STATUS_V1.UNPAID },
  {
    label: "Partially Paid",
    value: APPOINTMENT_PAYMENT_STATUS_V1.PARTIALLY_PAID,
  },
];

const UpdateAppointmentPaymentStatus = ({
  apt,
  className,
}: {
  apt: Appointment;
  className?: string;
}) => {
  const { aid } = apt;
  // @ts-expect-error [Appointment vs FirebaseAppointment as a type]
  const payment_status = apt?.payment?.status;
  const [selectedStatus, setSelectedStatus] = useState<
    APPOINTMENT_PAYMENT_STATUS_V1 | undefined
  >(undefined);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      try {
        const paymentStatus = await getPaymentStatusForAppointment({
          appointment: apt as unknown as FirebaseAppointment,
        });
        setSelectedStatus(paymentStatus);
      } catch (e) {
        console.error("Error fetching payment status for appointment", e);
      }
    };
    fetchPaymentStatus();
  }, [aid, payment_status]);

  const handleStatusClick = async (status: APPOINTMENT_PAYMENT_STATUS_V1) => {
    const newStatus = selectedStatus === status ? null : status;

    setSelectedStatus(newStatus || undefined);

    await updateAppointment({
      data: {
        //@ts-expect-error
        payment: newStatus ? { status: newStatus } : null,
      },
      aid,
    });

    setShowPopup(false);
  };

  const pillConfig = selectedStatus
    ? PAYMENT_STATUS_PILL_MAP[selectedStatus]
    : UNSET_PILL_CONFIG;

  return (
    <Popover
      placement="rightTop"
      trigger="click"
      className={`dr-cursor-pointer ${className || ""}`}
      open={showPopup}
      onOpenChange={(value) => setShowPopup(value)}
      content={
        <PopoverContainerUi className="dr-max-h-502 overflow-y-auto dr-max-w-408 dr-w-408">
          <PopoverHeader
            title="Payment Status"
            onCloseClick={() => setShowPopup(false)}
          />
          <div className="p-16">
            <div className="dr-flex dr-flex-wrap dr-gap-8">
              {WHITELISTED_PAYMENT_STATUSES.map((statusItem) => {
                const isSelected = selectedStatus === statusItem.value;
                return (
                  <Chip
                    key={statusItem.value}
                    title={statusItem.label}
                    className={`BodyBody2Regular ${
                      isSelected
                        ? "dr-bg-darwin-primary-bg-light dr-border-darwin-primary-primary dr-text-darwin-primary-primary"
                        : "dr-bg-darwin-touch-neutral-0 dr-border-darwin-touch-neutral-200 dr-text-darwin-touch-neutral-600"
                    }`}
                    onClick={() => handleStatusClick(statusItem.value)}
                  />
                );
              })}
            </div>
          </div>
        </PopoverContainerUi>
      }
      destroyTooltipOnHide={true}
      overlayClassName="fd-popup"
    >
      <div
        style={{
          fontSize: "10px",
        }}
        className={`dr-px-8 dr-py-2 dr-rounded-4 dr-border-1 dr-border-solid TouchLabelRegular dr-cursor-pointer text-10 dr-w-fit ${pillConfig?.className}`}
      >
        {pillConfig?.label}
      </div>
    </Popover>
  );
};

export default UpdateAppointmentPaymentStatus;